import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowLeft } from "lucide-react";

/**
 * AdminAnalytics — in-house dashboard showing key funnel metrics.
 * Only accessible to is_admin users; the backend rejects others.
 * Stylistically matches the rest of the app (brand tokens, serif
 * typography, no corporate dashboard feel).
 */
export default function AdminAnalytics() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [days, setDays] = useState(30);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setBusy(true);
        api.get(`/analytics/dashboard?days=${days}`)
            .then(({ data }) => { if (!cancelled) { setData(data); setErr(""); } })
            .catch((e) => {
                if (!cancelled) setErr(e?.response?.status === 403
                    ? "Accesso riservato agli amministratori."
                    : "Impossibile caricare i dati. Riprova fra un momento.");
            })
            .finally(() => { if (!cancelled) setBusy(false); });
        return () => { cancelled = true; };
    }, [days]);

    if (err) {
        return (
            <div className="min-h-screen bg-ink-paper flex items-center justify-center px-6">
                <div className="max-w-md text-center">
                    <p className="font-serif italic text-ink-textMid text-lg">{err}</p>
                    <button onClick={() => nav("/dashboard")} className="btn-secondary mt-8">Torna alla dashboard</button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ink-paper" data-testid="admin-analytics">
            <header className="px-6 md:px-12 py-8 border-b border-ink-textLow/10">
                <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
                    <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-ink-textLow hover:text-ink-textHigh transition-colors">
                        <ArrowLeft size={16} /> Torna alla dashboard
                    </Link>
                    <div className="flex items-center gap-2">
                        {[7, 30, 90].map((d) => (
                            <button
                                key={d}
                                onClick={() => setDays(d)}
                                className={`px-3 py-1.5 text-xs tracking-wider uppercase transition-colors ${
                                    days === d ? "text-ink-gold border-b border-ink-gold" : "text-ink-textLow hover:text-ink-textMid"
                                }`}
                                data-testid={`analytics-range-${d}`}
                            >
                                {d}g
                            </button>
                        ))}
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 md:px-12 py-10 md:py-14">
                <p className="label-eyebrow text-ink-gold">Analytics · {user?.name || "Admin"}</p>
                <h1 className="font-serif text-4xl md:text-5xl text-ink-textHigh mt-3 mb-2">
                    I numeri di Other Side.
                </h1>
                <p className="text-sm text-ink-textLow font-serif italic mb-12">
                    Ultimi {days} giorni. Visitatori, ricordi, abbonamenti.
                </p>

                {busy || !data ? (
                    <p className="text-sm text-ink-textLow font-serif italic">Caricamento…</p>
                ) : (
                    <>
                        {/* Totals row */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-14">
                            <BigStat label="Visitatori unici" value={data.totals.unique_visitors} testId="stat-visitors" />
                            <BigStat label="Ricordi gratis" value={data.totals.free_card_created} testId="stat-free-cards" />
                            <BigStat label="Iscrizioni" value={data.totals.signup_completed} testId="stat-signups" />
                            <BigStat label="Abbonamenti" value={data.totals.subscription_activated} testId="stat-subs" highlight />
                        </div>

                        {/* Funnel */}
                        <section className="mb-14">
                            <h2 className="font-serif text-xl text-ink-textHigh mb-1">Imbuto di conversione</h2>
                            <p className="text-xs text-ink-textLow font-serif italic mb-6">
                                Dal visitatore all'abbonato, passo per passo.
                            </p>
                            <div className="space-y-3" data-testid="funnel">
                                {data.funnel.map((f, i) => (
                                    <FunnelRow key={f.label} step={f} isLast={i === data.funnel.length - 1} />
                                ))}
                            </div>
                        </section>

                        {/* Sparklines */}
                        <section className="grid md:grid-cols-2 gap-8 mb-14">
                            <Sparkline title="Visitatori" data={data.sparklines.visitors} />
                            <Sparkline title="Ricordi gratis" data={data.sparklines.free_card_created} />
                            <Sparkline title="Iscrizioni" data={data.sparklines.signup_completed} />
                            <Sparkline title="Abbonamenti" data={data.sparklines.subscription_activated} />
                        </section>

                        {/* Top referrers + share split */}
                        <div className="grid md:grid-cols-2 gap-8">
                            <section>
                                <h2 className="font-serif text-xl text-ink-textHigh mb-1">Da dove arrivano</h2>
                                <p className="text-xs text-ink-textLow font-serif italic mb-4">
                                    I domini di provenienza più frequenti.
                                </p>
                                {(data.top_referrers || []).length === 0 ? (
                                    <p className="text-sm text-ink-textLow italic">Ancora nessun dato.</p>
                                ) : (
                                    <ul className="space-y-1 text-sm" data-testid="top-referrers">
                                        {data.top_referrers.map((r) => (
                                            <li key={r.host} className="flex items-center justify-between py-1.5 border-b border-ink-textLow/10">
                                                <span className="text-ink-textMid">{r.host}</span>
                                                <span className="text-ink-gold font-serif">{r.count}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </section>
                            <section>
                                <h2 className="font-serif text-xl text-ink-textHigh mb-1">Canali di condivisione</h2>
                                <p className="text-xs text-ink-textLow font-serif italic mb-4">
                                    Dove condividono i ricordi.
                                </p>
                                {(data.share_split || []).length === 0 ? (
                                    <p className="text-sm text-ink-textLow italic">Nessuna condivisione tracciata.</p>
                                ) : (
                                    <ul className="space-y-1 text-sm" data-testid="share-split">
                                        {data.share_split.map((s) => (
                                            <li key={s.platform} className="flex items-center justify-between py-1.5 border-b border-ink-textLow/10">
                                                <span className="text-ink-textMid capitalize">{s.platform}</span>
                                                <span className="text-ink-gold font-serif">{s.count}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </section>
                        </div>

                        <p className="mt-16 text-xs text-ink-textLow font-serif italic text-center">
                            Dati raccolti senza cookie, con consenso Do-Not-Track rispettato.
                            <br/>
                            Nessun IP salvato — solo hash giornalieri per il conteggio dei visitatori unici.
                        </p>
                    </>
                )}
            </main>
        </div>
    );
}

function BigStat({ label, value, testId, highlight }) {
    return (
        <div className={`p-5 border rounded-sm ${highlight ? "border-ink-gold/40 bg-ink-gold/5" : "border-ink-textLow/15"}`} data-testid={testId}>
            <p className="text-[10px] tracking-widest uppercase text-ink-textLow">{label}</p>
            <p className={`font-serif text-4xl mt-2 ${highlight ? "text-ink-gold" : "text-ink-textHigh"}`}>
                {value || 0}
            </p>
        </div>
    );
}

function FunnelRow({ step, isLast }) {
    // Bar width scales to the "rate" (% of visitors).
    const w = Math.max(4, Math.min(100, step.rate || 0));
    return (
        <div>
            <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-sm text-ink-textMid">{step.label}</span>
                <span className="text-sm font-serif">
                    <span className="text-ink-textHigh">{step.count}</span>
                    <span className="text-ink-textLow ml-2">· {step.rate}%</span>
                </span>
            </div>
            <div className="h-2 rounded-sm bg-ink-textLow/10 overflow-hidden">
                <div
                    className={`h-full transition-all duration-500 ${isLast ? "bg-ink-gold" : "bg-ink-gold/60"}`}
                    style={{ width: `${w}%` }}
                />
            </div>
        </div>
    );
}

function Sparkline({ title, data }) {
    const values = (data || []).map((d) => d.count);
    const max = Math.max(...values, 1);
    const total = values.reduce((a, b) => a + b, 0);
    return (
        <div>
            <p className="label-eyebrow text-ink-textLow mb-3">{title}</p>
            <p className="font-serif text-3xl text-ink-textHigh mb-3">{total}</p>
            <div className="flex items-end gap-1 h-10">
                {(data || []).map((d, i) => {
                    const h = Math.max(2, Math.round((d.count / max) * 38));
                    return (
                        <div
                            key={i}
                            className="flex-1 bg-ink-gold/60 rounded-sm"
                            style={{ height: `${h}px` }}
                            title={`${d.date}: ${d.count}`}
                        />
                    );
                })}
            </div>
        </div>
    );
}
